/**
 * IconLibraryService - Service for managing icon libraries and dynamic icon loading
 *
 * Features:
 * - Dynamic icon loading from lucide-react and @radix-ui/react-icons
 * - Fuzzy search with keyword matching
 * - Category filtering
 * - Performance-optimized with tree-shaking support
 * - Type-safe icon component loading
 */

import React from "react";
import { IconLibrary } from "@/types";
import iconMetadata from "@/data/icon-metadata.json";
import * as LucideIcons from "lucide-react";
import * as RadixIcons from "@radix-ui/react-icons";
import type { LucideIcon } from "lucide-react";
import type { IconProps } from "@radix-ui/react-icons/dist/types";
import type { ComponentType } from "react";
import { FermentationTankIcon } from "@/components/icons/FermentationTankIcon";

/**
 * Icon metadata structure
 */
export interface IconMetadata {
  name: string;
  displayName: string;
  library: "lucide" | "radix" | "custom";
  category: string;
  keywords: string[];
  tags: string[];
}

/**
 * Unified icon component type that works with both libraries
 */
export type UnifiedIconComponent = ComponentType<{
  size?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
}>;

/**
 * Search and filter options
 */
export interface IconSearchOptions {
  query?: string;
  category?: string;
  library?: IconLibrary;
  limit?: number;
}

/**
 * Custom icons registry
 */
const customIcons: Record<string, UnifiedIconComponent> = {
  FermentationTank: FermentationTankIcon,
  Tank: FermentationTankIcon,
};

/**
 * Custom icon metadata
 */
const customIconMetadata: IconMetadata[] = [
  {
    name: "FermentationTank",
    displayName: "Fermentation Tank",
    library: "custom",
    category: "industrial",
    keywords: ["fermentation", "tank", "brewery", "brewing", "vessel", "beer", "wine"],
    tags: ["equipment", "industrial", "agriculture", "manufacturing"],
  },
];

/**
 * Convert PascalCase to human-readable display name.
 * e.g., "ArrowBigDown" -> "Arrow Big Down"
 */
function pascalToDisplayName(name: string): string {
  return name.replace(/([A-Z])/g, " $1").trim();
}

/**
 * Derive search keywords from a PascalCase icon name.
 */
function pascalToKeywords(name: string): string[] {
  return name
    .replace(/([A-Z])/g, " $1")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0);
}

/**
 * Assign a category to an auto-discovered icon based on name patterns.
 */
function inferCategory(name: string): string {
  const lower = name.toLowerCase();
  const patterns: [RegExp, string][] = [
    [/arrow|chevron|move|corner|undo|redo|rotate|flip|iterate|repeat/, "arrows"],
    [/chart|bar|pie|graph|trending|line|area|scatter/, "charts"],
    [/mail|message|phone|send|inbox|at-sign|contact|chat|speech|bubble|reply/, "communication"],
    [/monitor|laptop|tablet|smartphone|cpu|hard|printer|screen|tv|display|keyboard|mouse/, "devices"],
    [/file|folder|clipboard|document|archive|save|paperclip|attachment/, "files"],
    [/menu|home|house|navigation|compass|map|pin|globe|route|waypoint|signpost/, "navigation"],
    [/image|camera|video|film|play|pause|stop|volume|music|mic|speaker|headphone/, "media"],
    [/user|person|people|group|team/, "social"],
    [/lock|shield|key|fingerprint|scan/, "security"],
    [/check|alert|ban|info|octagon|warning|error|bug/, "status"],
    [/clock|timer|calendar|hourglass|watch|alarm/, "time"],
    [/edit|pen|pencil|eraser|scissors|cut|copy|paste|type|text|font|bold|italic/, "editing"],
    [/sun|moon|cloud|rain|snow|wind|thermometer|umbrella|droplet|zap|flame/, "nature"],
    [/wifi|bluetooth|signal|radio|antenna|satellite|rss|cast|nfc/, "connectivity"],
  ];
  for (const [pattern, category] of patterns) {
    if (pattern.test(lower)) return category;
  }
  return "other";
}

/**
 * IconLibraryService - Main service class
 */
class IconLibraryService {
  private metadata: IconMetadata[];
  private iconCache: Map<string, UnifiedIconComponent> = new Map();

  constructor() {
    // Start with curated metadata (preserves rich keywords, categories, tags)
    const curated = [
      ...(iconMetadata as IconMetadata[]),
      ...customIconMetadata,
    ];

    // Build a Set of curated Lucide icon names for fast lookup
    const curatedLucideNames = new Set(
      curated.filter((m) => m.library === "lucide").map((m) => m.name)
    );

    // Auto-discover all canonical Lucide icons from the `icons` export
    const lucideIconsMap = (LucideIcons as any).icons as
      | Record<string, unknown>
      | undefined;
    const autoDiscovered: IconMetadata[] = [];

    if (lucideIconsMap) {
      for (const name of Object.keys(lucideIconsMap)) {
        if (curatedLucideNames.has(name)) continue;
        autoDiscovered.push({
          name,
          displayName: pascalToDisplayName(name),
          library: "lucide",
          category: inferCategory(name),
          keywords: pascalToKeywords(name),
          tags: [],
        });
      }
    }

    // Curated first (stable ordering), then auto-discovered (alphabetical)
    autoDiscovered.sort((a, b) => a.name.localeCompare(b.name));
    this.metadata = [...curated, ...autoDiscovered];
  }

  /**
   * Get all available icon metadata
   */
  getAllMetadata(): IconMetadata[] {
    return this.metadata;
  }

  /**
   * Get all unique categories
   */
  getCategories(): string[] {
    const categories = new Set(this.metadata.map((icon) => icon.category));
    return Array.from(categories).sort();
  }

  /**
   * Search icons with fuzzy matching
   */
  searchIcons(options: IconSearchOptions = {}): IconMetadata[] {
    let results = [...this.metadata];

    // Filter by library
    if (options.library) {
      results = results.filter((icon) => icon.library === options.library);
    }

    // Filter by category
    if (options.category) {
      results = results.filter((icon) => icon.category === options.category);
    }

    // Filter by search query
    if (options.query) {
      const query = options.query.toLowerCase().trim();
      results = results.filter((icon) => {
        // Match against display name
        if (icon.displayName.toLowerCase().includes(query)) {
          return true;
        }
        // Match against keywords
        if (icon.keywords.some((keyword) => keyword.toLowerCase().includes(query))) {
          return true;
        }
        // Match against tags
        if (icon.tags.some((tag) => tag.toLowerCase().includes(query))) {
          return true;
        }
        return false;
      });
    }

    // Apply limit
    if (options.limit && options.limit > 0) {
      results = results.slice(0, options.limit);
    }

    return results;
  }

  /**
   * Load an icon component dynamically
   * Returns a unified component that works with both libraries
   */
  async loadIcon(iconName: string, library: IconLibrary): Promise<UnifiedIconComponent | null> {
    const cacheKey = `${library}:${iconName}`;

    // Check cache first
    if (this.iconCache.has(cacheKey)) {
      return this.iconCache.get(cacheKey)!;
    }

    try {
      if (library === IconLibrary.LUCIDE) {
        return this.loadLucideIcon(iconName, cacheKey);
      } else if (library === IconLibrary.RADIX) {
        return this.loadRadixIcon(iconName, cacheKey);
      } else if (library === IconLibrary.CUSTOM) {
        return this.loadCustomIcon(iconName, cacheKey);
      }
    } catch (error) {
      console.error(`Failed to load icon ${iconName} from ${library}:`, error);
      return null;
    }

    return null;
  }

  /**
   * Load a custom icon
   */
  private loadCustomIcon(iconName: string, cacheKey: string): UnifiedIconComponent | null {
    const IconComponent = customIcons[iconName];

    if (!IconComponent) {
      console.warn(`Custom icon "${iconName}" not found`);
      return null;
    }

    this.iconCache.set(cacheKey, IconComponent);
    return IconComponent;
  }

  /**
   * Load a Lucide icon
   */
  private loadLucideIcon(iconName: string, cacheKey: string): UnifiedIconComponent | null {
    const IconComponent = (LucideIcons as any)[iconName] as LucideIcon | undefined;

    if (!IconComponent) {
      console.warn(`Lucide icon "${iconName}" not found`);
      return null;
    }

    // Wrap Lucide icon to support unified props (Lucide icons are ForwardRefExoticComponent)
    const unifiedComponent: UnifiedIconComponent = ({ size = 24, color, strokeWidth, className, style }) => {
      return React.createElement(IconComponent, {
        size,
        color,
        strokeWidth,
        className,
        style,
      });
    };

    this.iconCache.set(cacheKey, unifiedComponent);
    return unifiedComponent;
  }

  /**
   * Load a Radix icon
   */
  private loadRadixIcon(iconName: string, cacheKey: string): UnifiedIconComponent | null {
    const IconComponent = (RadixIcons as any)[iconName] as ComponentType<IconProps> | undefined;

    if (!IconComponent) {
      return null;
    }

    // Wrap Radix icon to support unified props
    const unifiedComponent: UnifiedIconComponent = ({ size = 24, color, className, style }) => {
      return React.createElement(IconComponent, {
        width: size,
        height: size,
        color,
        className,
        style,
      } as IconProps);
    };

    this.iconCache.set(cacheKey, unifiedComponent);
    return unifiedComponent;
  }

  /**
   * Get icon metadata by name
   */
  getIconMetadata(iconName: string, library: IconLibrary): IconMetadata | null {
    return this.metadata.find(
      (icon) => icon.name === iconName && icon.library === library
    ) || null;
  }

  /**
   * Get icon count by library
   */
  getIconCount(library?: IconLibrary): number {
    if (library) {
      return this.metadata.filter((icon) => icon.library === library).length;
    }
    return this.metadata.length;
  }

  /**
   * Clear icon cache (useful for memory management)
   */
  clearCache(): void {
    this.iconCache.clear();
  }

  /**
   * Preload frequently used icons
   */
  async preloadIcons(icons: Array<{ name: string; library: IconLibrary }>): Promise<void> {
    const promises = icons.map(({ name, library }) => this.loadIcon(name, library));
    await Promise.allSettled(promises);
  }
}

// Export singleton instance
export const iconLibraryService = new IconLibraryService();
